import { Editor } from '@monaco-editor/react';
import { Moon, Sun } from 'lucide-react';
import Map from 'images/menu_icon/company_map_blue.svg';
import Box from 'images/menu_icon/product_menu_blue.svg';
import Event from 'images/menu_icon/event_blue.svg';
import { isArray, set } from 'lodash';
import * as React from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { getFormTemplates, getLastStartedAtFormInstance } from '../../atoms/forms';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { compressImageToBase64 } from '../../components_v2/dropdown/DropdownColors';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import Input from '../../components_v2/input/Input';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { PanelSelector } from '../../components_v2/Selector/PanelSelector';
import { getUrlParameterByName } from '../../components_v2/utils';
import { getLocalStorage } from '../../helpers/localStorage';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, DarkGreySidely2, GreySidely2, LightGreySidely, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useKeyPress } from '../../utils/customHooks';
import { ToolbarBox } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import PageSwitch from '../map/PageSwitch';
import { FlexDiv } from '../products/style';
import { getFormViewById, NewView, postFormView, putFormView, putFormViewRelations, View } from './actions';
import bonDeCommandeRayons from './form-views/bon-de-commande-rayons.html';
import bonDeCommande from './form-views/bon-de-commande.html';
import planDeVenteRayons from './form-views/plan-de-vente-rayons.html';
import planDeVente from './form-views/plan-de-vente.html';
import planDeVenteRayonsCategories from './form-views/plan-de-vente-rayons-categories.html';
import planDeVenteCategories from './form-views/plan-de-vente-categories.html';
import campagnes from './form-views/campagnes.html';
import { FormTemplate } from './jsonValidator';
import { TemplateRenderer } from './templateRenderer';


const StyledLabel = styled.div`
	font-size: 12px;
	color: ${DarkGreySidely2};
	text-wrap: nowrap;
`;

function getDiffFormView(oldFormView: View, newFormView: View | NewView) {
	const diffs: Partial<View> = {};

	for (const key in oldFormView) {
		if (key !== 'linked_form_ids' && oldFormView[key] !== newFormView[key]) {
			diffs[key] = newFormView[key];
		}
	}

	return diffs;
}

function getUpdatedLinkedForms(newLinkedForms: number[], oldLinkedForms?: number[]) {
	let addedLinkedForms: number[] = [];
	const removedLinkedForms: number[] = [];
	if (oldLinkedForms) {
		if (oldLinkedForms) {
			oldLinkedForms.forEach((formId) => {
				if (!newLinkedForms.includes(formId)) {
					removedLinkedForms.push(formId);
				}
			});
		}

		if (newLinkedForms) {
			newLinkedForms.forEach((formId) => {
				if (!oldLinkedForms.includes(formId)) {
					addedLinkedForms.push(formId);
				}
			});
		}
	}
	else
		addedLinkedForms = newLinkedForms;

	return { addedLinkedForms, removedLinkedForms };
}

function debounce<T extends(...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
	let timeoutId: NodeJS.Timeout | null = null;

	return (...args: Parameters<T>) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			func(...args);
		}, delay);
	};
}

const TemplatesContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, 180px);
	height: 100%;
	overflow-y: auto;
	background-color: ${SurfaceColor};
	gap: 16px;
	padding: 16px;
`;

const TemplateItem = styled.div<{ selected?: boolean, hidden?: boolean }>`
	display: ${props => props.hidden ? 'none' : 'flex'};
	flex-direction: column;
	justify-content: center;
	align-items: center;
	gap: 12px;
	font-size: 12px;
	padding: 8px;
	height: ${props => props.selected ? '100%' : '220px'};
	width: ${props => props.selected ? 'calc(100vw - 116px)' : '180px'};
	border-radius: 5px;
	font-size: 13px;
	box-shadow: 0px 0px 5px 1px color-mix(in oklab, ${SidelyBlack} 8%, transparent);
`;

const TemplateLogo = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 36px;
	aspect-ratio: 1;
	background-color: color-mix(in oklab, ${PrimaryColor} 20%, transparent);
	border-radius: 20px;
	img {
		width: 24px;
	}
`;

const TemplateCustomization = styled.div`
	width: 100%;
	height: 100%;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	padding: 16px;
	gap: 24px;
	font-size: 12px;
`;

const CustomizationTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: ${DarkGreySidely2};
`;

type EditorColor = 'light' | 'vs-dark';

export default function FormViewTemplateCreator(props: ComponentProps) {
	const history = useHistory();
	const viewId = parseInt(getUrlParameterByName('id') ?? 'NaN');
	const [oldFormView, setOldFormView] = React.useState<View | undefined>(undefined);
	const [formView, setFormView] = React.useState<View | NewView | undefined>(undefined);
	const [templates, setTemplates] = React.useState<FormTemplate[]>([]);
	const [refresh, setRefresh] = React.useState(false);
	const [formViewSequence, setFormViewSequence] = React.useState<number | undefined>(undefined);
	const [editorColorMode, setEditorColorMode] = React.useState<EditorColor>(getLocalStorage('isDarkMode') ? 'vs-dark' : 'light');
	const [lastFormInstanceUuid, setLastFormInstanceUuid] = React.useState<string | undefined>(undefined);
	const [panels, setPanels] = React.useState<{ label: string }[]>([{ label: 'Jinja' }]);
	const [activePanel, setActivePanel] = React.useState<string>(panels[0].label);
	const [selectedTemplate, setSelectedTemplate] = React.useState<string | undefined>(undefined);
	const [logoBase64, setLogoBase64] = React.useState<string>('');
	const [mainColor, setMainColor] = React.useState<string>('');
	const [clientName, setClientName] = React.useState<string>('');
	const [adresses, setAdresses] = React.useState<string[]>(['', '', '', '']);
	const [hideTemplate, setHideTemplate] = React.useState(false);
	const diffs = oldFormView && formView ? (getDiffFormView(oldFormView, formView)) : {};
	const enableSave = formView && formViewSequence !== undefined && formViewSequence > 0 && formView.name.length;
	const viewTemplates = [
		{ label: 'Plan de vente', content: planDeVente, icon: Map },
		{ label: 'Plan de vente (catégories)', content: planDeVenteCategories, icon: Map },
		{ label: 'Plan de vente (rayons)', content: planDeVenteRayons, icon: Map },
		{ label: 'Plan de vente (rayons + catégories)', content: planDeVenteRayonsCategories, icon: Map },
		{ label: 'Bon de commande', content: bonDeCommande, icon: Box },
		{ label: 'Bon de commande (rayons)', content: bonDeCommandeRayons, icon: Box },
		{ label: 'Campagnes', content: campagnes, icon: Event }
	];
	const fileInputRef = React.useRef<HTMLInputElement | null>(null);

	const debouncedSetFormView = React.useMemo(() => debounce((updatedTemplate: string) => {
		setFormView((prevFormView) => {
			if (prevFormView) {
				return { ...prevFormView, template: updatedTemplate };
			}
			return prevFormView;
		});
	}, 500), []);

	React.useEffect(() => {
		setPanels(!formView?.id && !hideTemplate ? [{ label: 'Templates' }, { label: 'Jinja' }, { label: translateToString('vizualization') }] : [{ label: 'Jinja' }, { label: translateToString('vizualization') }]);
		setActivePanel(!formView?.id && !hideTemplate ? 'Templates' : 'Jinja');
	}, [formView?.id, hideTemplate]);


	React.useEffect(() => {
		if (viewId) {
			getFormViewById(viewId).then((res) => {
				setFormView(res);
				setOldFormView(res);
				setFormViewSequence(res.sequence);
			});
		}
		else {
			setFormView({ id: undefined, name: '', template: '', sequence: undefined, linked_form_ids: [] });
		}
		getFormTemplates().then((res) => setTemplates(res.allForms));
		setRefresh(false);
	}, [refresh]);

	React.useEffect(() => {
		if (formView?.linked_form_ids && formView.linked_form_ids.length)
			getLastStartedAtFormInstance(formView.linked_form_ids[0]).then((res) => { if (res) setLastFormInstanceUuid(res.uuid); });
	}, [formView?.linked_form_ids]);

	const saveFormView = React.useCallback((quit: boolean) => async() => {
		if (!formView) return;
		const updatedLinkedForms: { addedLinkedForms: Array<number>, removedLinkedForms: Array<number> } | undefined = formView ? getUpdatedLinkedForms(formView.linked_form_ids, oldFormView?.linked_form_ids) : undefined;
		if (formView.id) {
			putFormView({ ...diffs, id: formView.id }).then(() => setRefresh(true));
			if (updatedLinkedForms && (updatedLinkedForms.addedLinkedForms.length || updatedLinkedForms.removedLinkedForms.length)) {
				putFormViewRelations({ id: formView.id, added_linked_forms: updatedLinkedForms.addedLinkedForms ?? null, removed_linked_forms: updatedLinkedForms.removedLinkedForms ?? null });
			}
		}
		else if (formView.sequence !== undefined) {
			postFormView({ name: formView.name, sequence: formView.sequence, template: formView.template }).then((viewId) => {
				if (viewId && updatedLinkedForms && (updatedLinkedForms.addedLinkedForms.length || updatedLinkedForms.removedLinkedForms.length)) {
					putFormViewRelations({ id: parseInt(viewId), added_linked_forms: updatedLinkedForms.addedLinkedForms ?? null, removed_linked_forms: updatedLinkedForms.removedLinkedForms ?? null });
				}
			});
		}
		if (quit)
			history.push({ pathname: 'settings', hash: 'form_views' });
	}, [formView]);

	const handleButtonClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleLogoUpload = async(event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file && file.type.startsWith('image/')) {
			try {
				const base64 = await compressImageToBase64(file, 300);
				setLogoBase64(base64);
			} catch (error) {
				console.error('Error compressing the image:', error);
			}
		}
	};

	const formatTemplate = (template: string) => {
		template = template.replace(/#mainColor#/g, mainColor);
		template = template.replace(/#logoBase64#/g, logoBase64);
		template = template.replace(/#clientName#/g, clientName);
		template = template.replace(/#adresses#/g, adresses.map((address) => `<li>${address}</li>`).join(''));
		return template;
	};

	const formTemplateList: DropdownData[] = React.useMemo(
		() => templates.map((template) => {
			return { label: template.name, value: template.id, checked: formView && formView.linked_form_ids ? formView.linked_form_ids.includes(template.id) : false };
		}), [formView, templates]);

	useKeyPress([{ key: 's', metaKey: true }], () => {
		if (enableSave)
			saveFormView(false)();
	});

	React.useEffect(() => {
		if (formView) {
			props.setToolBarState({
				title: translateToString('form_views'),
				bottomLeftToolbarComponent: <ToolbarBox>
					<Input inputStyle={{
						removePlaceHolderOnFocus: true,
						width: '200px',
						backgroundColor: LightGreySidely,
						borderRadius: '15px',
						color: DarkGreySidely2,
						placeholderColor: GreySidely2,
						hasInputBorder: true,
						border: 'none',
						textOverflow: 'ellipsis',
						containerWidth: '100%',
						padding: '10px'
					}}
					name='view-name' spellcheck={false} type='text' value={formView.name} onChange={(value) => {
						setFormView({ ...formView, name: value.trim() });
					}} />
					<FlexDiv gap={'4px'}>
						<StyledLabel><Translate id='form_view_templates.linked_forms' />:</StyledLabel>
						<Dropdown
							name='dropdown_from_templates'
							datalist={formTemplateList}
							onChange={(value) => {
								if (!isArray(value))
									return;
								setFormView({ ...formView, linked_form_ids: value.map((form) => form.value) });
							}}
							isMulti
							checkbox
							doNotDeleteOptionOnSelect
							dropdownStyle={{
								width: '270px',
								optionHeight: '300px'
							}}
						/>
					</FlexDiv>
					<FlexDiv gap={'4px'}>
						<StyledLabel><Translate id='form_view_templates.sequence' />:</StyledLabel>
						<Input
							name='input_form_template_sequence'
							type='number'
							value={formView.sequence}
							onChange={(value) => {
								if (value && value.length) {
									const valueInt = parseInt(value, 10);
									if (!isNaN(valueInt)) {
										setFormView({ ...formView, sequence: valueInt });
										setFormViewSequence(valueInt);
									}
									else
										setFormViewSequence(undefined);
								}
								else setFormViewSequence(undefined);
							}}
							inputStyle={{
								width: '50px',
								borderRadius: '5px'
							}}
						/>
					</FlexDiv>
					<PageSwitch<EditorColor>
						background={true}
						selectedValue={editorColorMode}
						list={[
							{ value: 'light', img: <Sun />, onClick: (value) => { setEditorColorMode(value); localStorage.setItem('isDarkMode', JSON.stringify(value === 'vs-dark')); } },
							{ value: 'vs-dark', img: <Moon />, onClick: (value) => { setEditorColorMode(value); localStorage.setItem('isDarkMode', JSON.stringify(value === 'vs-dark')); } }
						]} />
				</ToolbarBox>,
				bottomRightToolbarComponent: <FlexDiv gap='8px'>
					<DefaultButton
						onClick={() => history.push({ pathname: 'settings', hash: 'form_views' })}
						buttonStyle={ButtonStyle.White}
						margin='0px'
					>
						<Translate id='back' />
					</DefaultButton>
					{!!formView.id &&
						<DefaultButton
							onClick={saveFormView(false)}
							margin='0px'
							disabled={!enableSave}
						>
							<Translate id='save' />
						</DefaultButton>}
					<DefaultButton
						onClick={saveFormView(true)}
						margin='0px'
						disabled={!enableSave}

					>
						<Translate id='save_and_quit' />
					</DefaultButton>
				</FlexDiv>
			});
		}
	}, [formView, editorColorMode, templates, enableSave]);

	return <div style={{ height: 'calc(100vh - 150px)' }}>
		<PanelSelector
			borderBottom
			panels={panels}
			activeIndex={panels.findIndex((panel) => panel.label === activePanel)}
			onClick={(panel) => {
				setActivePanel(panel.label);
			}}
		/>
		{activePanel === 'Templates' && !hideTemplate &&
			<TemplatesContainer>
				{viewTemplates.map((template, index) => {
					return <TemplateItem key={`${template.label}${index}`} selected={selectedTemplate === template.label} hidden={selectedTemplate !== undefined && selectedTemplate !== template.label}>
						{selectedTemplate !== template.label ?
							<>
								<TemplateLogo><img src={template.icon} /></TemplateLogo>
								<span style={{ textAlign: 'center', height: '30px' }}>{template.label}</span>
								<DefaultButton onClick={() => {
									setSelectedTemplate(template.label);
								}}>
									<Translate id='form_view_templates.use_template' />
								</DefaultButton>
							</>
							:
							<TemplateCustomization>
								<CustomizationTitle><Translate id='form_view_templates.customization_title' /> : {template.label}</CustomizationTitle>
								<FlexDiv gap='16px' flow='column' align='stretch'>
									<FlexDiv gap='8px'>
										<span>
											<Translate id='form_view_templates.client_name_label' /> :
										</span>
										<Input inputStyle={{ borderRadius: '5px' }} name='form_client_name' type='text' onChange={(value) => setClientName(value)} />
									</FlexDiv>
									<FlexDiv gap='8px'>
										<span>
											<Translate id='form_view_templates.color_label' /> :
										</span>
										<input type='color' onChange={(event) => setMainColor(event.target.value)} />
									</FlexDiv>
									<FlexDiv gap='8px' flow='column' align='stretch'>
										<FlexDiv gap='8px'>
											<span><Translate id='form_view_templates.logo_label' /> : </span>
											<DefaultButton onClick={handleButtonClick} margin='0px' buttonStyle={ButtonStyle.White}>
												<Translate id='form_view_templates.upload_logo' />
											</DefaultButton>
											<input
												ref={fileInputRef}
												type='file'
												accept='image/*'
												onChange={handleLogoUpload}
												style={{ display: 'none' }}
											/>
										</FlexDiv>
										{logoBase64 && (
											<FlexDiv gap='8px' style={{ marginTop: '16px' }}>
												<img src={logoBase64} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '5px', boxShadow: `0px 0px 5px color-mix(in oklab, ${SidelyBlack} 10%, transparent)` }} />
											</FlexDiv>
										)}
									</FlexDiv>
									<FlexDiv flow='column' gap='8px' align='stretch'>
										<span><Translate id='form_view_templates.adress_label' /> : </span>
										<FlexDiv flow='column' gap='8px' align='stretch'>
											{[0, 1, 2, 3].map((i) =>
												<FlexDiv key={i}>
													<span style={{ width: '60px' }}>
														<Translate id='line' /> {i + 1}
													</span>
													<Input inputStyle={{ borderRadius: '5px' }} name={`form_adress_${i}`} type='text' onChange={(value) => {
														const newAdresses = [...adresses];
														set(newAdresses, i, value);
														setAdresses(newAdresses);
													}} />
												</FlexDiv>
											)
											}
										</FlexDiv>
									</FlexDiv>
								</FlexDiv>
								<FlexDiv gap='16px'>
									<DefaultButton
										buttonStyle={ButtonStyle.White}
										margin='0px'
										width='fit-content'
										onClick={() => {
											setSelectedTemplate(undefined);
											setLogoBase64('');
											setMainColor('');
											setClientName('');
											setAdresses(['', '', '', '']);
										}}
									>
										<Translate id='back' />
									</DefaultButton>
									<DefaultButton
										buttonStyle={ButtonStyle.Default}
										margin='0px'
										width='fit-content'
										onClick={() => {
											const formattedTemplate = formatTemplate(template.content);
											setFormView({
												...formView,
												template: formattedTemplate,
												name: formView?.name || '',
												linked_form_ids: formView?.linked_form_ids || []
											});
											setSelectedTemplate(undefined);
											setHideTemplate(true);
										}}
									>
										<Translate id='form_view_templates.load_template' />
									</DefaultButton>
								</FlexDiv>
							</TemplateCustomization>
						}
					</TemplateItem>;
				})}
			</TemplatesContainer>
		}
		{formView &&
			<div style={{ height: '100%', display: activePanel === 'Jinja' ? 'block' : 'none' }}>
				<Editor
					height='100%'
					value={formView.template}
					language='html'
					onChange={(value) => {
						if (value)
							debouncedSetFormView(value);
					}}
					theme={editorColorMode}
				/>
			</div>
		}
		{formView && lastFormInstanceUuid && activePanel === translateToString('vizualization') &&
			<div style={{ backgroundColor: SurfaceColor, width: '100%', height: '100%' }}>
				<TemplateRenderer template={formView.template} form_instance_uuid={lastFormInstanceUuid} height={'calc(100% - 10px)'} />
			</div>
		}
	</div>;
}
